import axios from "axios";
import { API } from "./api";

// Cookie-authenticated client with a one-shot refresh on 401.
export const authHttp = axios.create({ baseURL: API, timeout: 60000, withCredentials: true });

let refreshing = null;
authHttp.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { config, response } = error;
    const url = config?.url || "";
    if (response?.status !== 401 || config._retried || url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/mfa/verify")) {
      throw error;
    }
    config._retried = true;
    try {
      refreshing = refreshing || authHttp.post("/auth/refresh").finally(() => (refreshing = null));
      await refreshing;
    } catch {
      throw error;
    }
    return authHttp(config);
  }
);

const data = (p) => p.then((r) => r.data);

export const authApi = {
  me: () => data(authHttp.get("/auth/me")),
  register: (body) => data(authHttp.post("/auth/register", body)),
  login: (body) => data(authHttp.post("/auth/login", body)),
  mfaVerify: (body) => data(authHttp.post("/auth/mfa/verify", body)),
  logout: () => data(authHttp.post("/auth/logout")),
  changePassword: (body) => data(authHttp.post("/auth/change-password", body)),
  mfaSetup: () => data(authHttp.post("/auth/mfa/setup")),
  mfaEnable: (code) => data(authHttp.post("/auth/mfa/enable", { code })),
  mfaDisable: (body) => data(authHttp.post("/auth/mfa/disable", body)),
  mfaRecoveryCodes: (code) => data(authHttp.post("/auth/mfa/recovery-codes", { code })),

  org: {
    get: () => data(authHttp.get("/org")),
    patch: (body) => data(authHttp.patch("/org", body)),
    alerts: () => data(authHttp.get("/org/alerts")),
    testAlert: () => data(authHttp.post("/org/alerts/test")),
    users: () => data(authHttp.get("/org/users")),
    createUser: (body) => data(authHttp.post("/org/users", body)),
    patchUser: (id, body) => data(authHttp.patch(`/org/users/${id}`, body)),
    resetPassword: (id) => data(authHttp.post(`/org/users/${id}/reset-password`)),
    resetMfa: (id) => data(authHttp.post(`/org/users/${id}/reset-mfa`)),
    deleteUser: (id) => data(authHttp.delete(`/org/users/${id}`)),
  },

  tenants: {
    list: () => data(authHttp.get("/tenants")),
    get: (id) => data(authHttp.get(`/tenants/${id}`)),
    create: (body) => data(authHttp.post("/tenants", body)),
    patch: (id, body) => data(authHttp.patch(`/tenants/${id}`, body)),
    remove: (id) => data(authHttp.delete(`/tenants/${id}`)),
    verify: (id) => data(authHttp.post(`/tenants/${id}/verify`)),
    scan: (id) => data(authHttp.post(`/tenants/${id}/scan`)),
    scans: (id) => data(authHttp.get(`/tenants/${id}/scans`)),
    compare: (id, from, to) => data(authHttp.get(`/tenants/${id}/compare`, { params: { from, to } })),
    scenarios: (id) => data(authHttp.get(`/tenants/${id}/scenarios`)),
    createScenario: (id, body) => data(authHttp.post(`/tenants/${id}/scenarios`, body)),
    updateScenario: (id, sid, body) => data(authHttp.put(`/tenants/${id}/scenarios/${sid}`, body)),
    deleteScenario: (id, sid) => data(authHttp.delete(`/tenants/${id}/scenarios/${sid}`)),
    shareScenario: (id, sid) => data(authHttp.post(`/tenants/${id}/scenarios/${sid}/share`)),
    unshareScenario: (id, sid) => data(authHttp.delete(`/tenants/${id}/scenarios/${sid}/share`)),
    runSchedule: (id) => data(authHttp.post(`/tenants/${id}/schedule/run`)),
  },

  notifications: {
    list: () => data(authHttp.get("/notifications")),
    markRead: (ids = []) => data(authHttp.post("/notifications/read", { ids })),
  },
  scans: {
    get: (id) => data(authHttp.get(`/scans/${id}`)),
    remove: (id) => data(authHttp.delete(`/scans/${id}`)),
    users: (id, params) => data(authHttp.get(`/scans/${id}/users`, { params })),
    usersCsv: (id, params) => data(authHttp.get(`/scans/${id}/users/export`, { params, responseType: "blob" })),
  },
  imports: {
    resolveProducts: (names) => data(authHttp.post("/imports/resolve-products", { names })),
    create: (body) => data(authHttp.post("/imports", body)),
    addRows: (id, rows) => data(authHttp.post(`/imports/${id}/rows`, { rows })),
    commit: (id) => data(authHttp.post(`/imports/${id}/commit`)),
    cancel: (id) => data(authHttp.delete(`/imports/${id}`)),
  },
  compliance: {
    catalog: () => data(authHttp.get("/compliance/catalog")),
    overview: () => data(authHttp.get("/compliance/overview")),
    start: (tenantId) => data(authHttp.post(`/tenants/${tenantId}/compliance/scan`)),
    scans: (tenantId) => data(authHttp.get(`/tenants/${tenantId}/compliance/scans`)),
    trend: (tenantId, params) => data(authHttp.get(`/tenants/${tenantId}/compliance/trend`, { params })),
    scan: (scanId, params) => data(authHttp.get(`/compliance/scans/${scanId}`, { params })),
    attest: (tenantId, controlId, body) => data(authHttp.put(`/tenants/${tenantId}/compliance/attestations/${controlId}`, body)),
    clearAttestation: (tenantId, controlId) => data(authHttp.delete(`/tenants/${tenantId}/compliance/attestations/${controlId}`)),
    assignOwner: (tenantId, controlId, body) => data(authHttp.put(`/tenants/${tenantId}/compliance/owners/${controlId}`, body)),
    clearOwner: (tenantId, controlId) => data(authHttp.delete(`/tenants/${tenantId}/compliance/owners/${controlId}`)),
    uploadEvidence: (tenantId, controlId, file) => {
      const form = new FormData();
      form.append("file", file, file.name);
      return data(authHttp.post(`/tenants/${tenantId}/compliance/evidence/${controlId}`, form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }));
    },
    evidenceBlob: (id) => data(authHttp.get(`/compliance/evidence/${id}`, { responseType: "blob", timeout: 60000 })),
    deleteEvidence: (id) => data(authHttp.delete(`/compliance/evidence/${id}`)),
    report: (scanId, params) => data(authHttp.get(`/compliance/scans/${scanId}/report`, { params, responseType: "blob", timeout: 120000 })),
  },
};
